/**
 * PostHog Session ID Header Propagation
 *
 * Enables session replay correlation between client-side and server-side events.
 * When server-side events (Analysis_Submit, LLM_RequestCompleted) include the
 * client's session_id, they appear in the PostHog session replay timeline,
 * enabling "what did the user do right before this backend event?" debugging.
 *
 * Security Considerations:
 * - Session IDs are NOT secrets (they're analytics correlation tokens)
 * - Headers are validated on server-side (UUID format check)
 * - Only accepted on authenticated requests (Supabase JWT present)
 *
 * Architecture Decision (2026-01-23):
 * Multi-model consensus (Gemini 3 Pro: 9/10, GPT 5.2 Pro: 8/10) validated
 * explicit header propagation over __add_tracing_headers due to React Native
 * compatibility and reliability across Supabase Edge Functions.
 *
 * @see OBSERVABILITY_HUB.md - "PostHog Session Linking" section
 */

import { logger } from '../logger';
import { getPostHogCache } from './posthog-cache';

/**
 * PostHog telemetry headers for session/user correlation
 */
export interface TelemetryHeaders {
  /**
   * PostHog session ID for session replay correlation
   * Format: UUID v4 (e.g., "a1b2c3d4-e5f6-7890-abcd-ef1234567890")
   */
  'x-posthog-session-id'?: string;

  /**
   * PostHog distinct ID for user identity correlation
   * Format: UUID v4 or custom user ID
   */
  'x-posthog-distinct-id'?: string;
}

/**
 * Get PostHog telemetry headers for API requests.
 *
 * This function safely extracts the current session ID and distinct ID from
 * PostHog SDK and returns them as HTTP headers to be propagated to Edge Functions.
 *
 * Server-side code can then extract these headers and include them in PostHog
 * capture() calls via the `$session_id` property (note the $ prefix!).
 *
 * Usage:
 * ```typescript
 * const headers = getTelemetryHeaders();
 * await supabase.functions.invoke('analyze-drama', {
 *   body: { message: '...' },
 *   headers: { 'Accept-Version': 'v2', ...headers, ...otherHeaders }
 * });
 * ```
 *
 * Server-side extraction:
 * ```typescript
 * const sessionId = req.headers.get('x-posthog-session-id');
 * posthog.capture({
 *   event: 'Analysis_Submit',
 *   properties: {
 *     '$session_id': sessionId, // <-- Links to session replay
 *     ...otherProperties
 *   }
 * });
 * ```
 *
 * @returns Telemetry headers object (empty if PostHog not initialized)
 */
export function getTelemetryHeaders(): TelemetryHeaders {
  const headers: TelemetryHeaders = {};

  // Check if running in browser/client environment
  if (typeof window === 'undefined') {
    return headers;
  }

  try {
    // Web implementation: PostHog loaded via script snippet
    if ((window as any).posthog) {
      const posthog = (window as any).posthog;

      // NOTE: No __loaded guard here. Before array.js loads, window.posthog is a
      // stub whose get_session_id() and get_distinct_id() return undefined (stubs
      // push to buffer, no return value). The checks below (sessionId && typeof ===
      // 'string') safely handle the undefined case without blocking. The old guard
      // was causing both headers to be dropped whenever PostHog hadn't loaded yet.

      // Get session ID (available once PostHog has loaded and session recording is active)
      if (typeof posthog.get_session_id === 'function') {
        const sessionId = posthog.get_session_id();
        if (sessionId && typeof sessionId === 'string') {
          headers['x-posthog-session-id'] = sessionId;
        } else if (__DEV__) {
          // Gap 4 diagnostic: detect race window where session recording hasn't started
          logger.debug('[Telemetry] get_session_id() returned null/undefined — session recording not yet active');
        }
      }

      // Get distinct ID (user identifier)
      if (typeof posthog.get_distinct_id === 'function') {
        const distinctId = posthog.get_distinct_id();
        if (distinctId && typeof distinctId === 'string') {
          headers['x-posthog-distinct-id'] = distinctId;
        } else if (__DEV__) {
          logger.debug('[Telemetry] get_distinct_id() returned null/undefined — PostHog stub not yet loaded');
        }
      }
    }
  } catch (error) {
    // Silently fail - telemetry propagation is best-effort, not critical
    if (__DEV__) {
      logger.debug('[Telemetry] Failed to get PostHog headers:', error);
    }
  }

  // Cache fallback: use stored values if live PostHog methods returned nothing
  // (e.g. array.js still loading, or PostHog not initialized due to missing env var)
  if (!headers['x-posthog-session-id'] || !headers['x-posthog-distinct-id']) {
    const { distinctId, sessionId } = getPostHogCache();
    if (!headers['x-posthog-session-id'] && sessionId) {
      headers['x-posthog-session-id'] = sessionId;
    } else if (__DEV__ && !headers['x-posthog-session-id']) {
      // Gap 4 diagnostic: both live and cached session ID are missing
      logger.debug('[Telemetry] x-posthog-session-id missing — live get_session_id() null AND cache empty (race window hit)');
    }
    if (!headers['x-posthog-distinct-id'] && distinctId) {
      headers['x-posthog-distinct-id'] = distinctId;
    } else if (__DEV__ && !headers['x-posthog-distinct-id']) {
      logger.debug('[Telemetry] x-posthog-distinct-id missing — live get_distinct_id() null AND cache empty');
    }
  }

  if (__DEV__) {
    logger.debug('[Telemetry] getTelemetryHeaders() result', {
      hasSessionId: !!headers['x-posthog-session-id'],
      hasDistinctId: !!headers['x-posthog-distinct-id'],
    });
  }

  return headers;
}

/**
 * Native implementation placeholder for React Native.
 *
 * This will be implemented in telemetry-headers.native.ts using posthog-react-native.
 * Metro bundler automatically selects the .native.ts file for React Native builds.
 *
 * @internal
 */
export function getTelemetryHeadersNative(): TelemetryHeaders {
  // This will be overridden by telemetry-headers.native.ts
  return {};
}
